import React from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart } from 'recharts';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { Box, Flex, Grid, Typography, useThemedValueSwitch } from '@tq/ui-components';
import { searchViewTypeState } from 'store';
import { CUSTOM_COLOURS, CUSTOM_TQ_COLOURS } from 'styles';
import { ConsultationViewTypes, Statistics } from 'types';
import { useRecoilState } from 'recoil';

type StatisticsChartProps = {
  statistics: Statistics;
  title: string;
};

type Statistic = {
  name: string;
  value: number;
  percentage: number;
  colour: string;
  badgeColour: string;
};

export const StatisticsChart: React.FC<StatisticsChartProps> = ({ statistics, title }) => {
  const { t } = useTranslation('search_statistics');
  const { tvs, isTQ } = useThemedValueSwitch();
  const [viewType] = useRecoilState(searchViewTypeState);
  const isTQArchived = isTQ && viewType === ConsultationViewTypes.Archived;

  const data: Statistic[] = [
    {
      name: isTQArchived ? t('responded', 'Responded') : t('on_track', 'On track'),
      value: isTQArchived ? statistics.respondedCount : statistics.onTrackCount,
      percentage: isTQArchived ? statistics.respondedPercentage : statistics.onTrackPercentage,
      colour: isTQ ? CUSTOM_TQ_COLOURS.darkBlue0D4E8A : CUSTOM_COLOURS.mediumWCAGGreen,
      badgeColour: COLOURS.white,
    },
    {
      name: isTQArchived ? t('closed', 'Closed') : t('overdue', 'Overdue'),
      value: isTQArchived ? statistics.closedCount : statistics.overdueCount,
      percentage: isTQArchived ? statistics.closedPercentage : statistics.overduePercentage,
      colour: isTQ ? CUSTOM_TQ_COLOURS.darkGreen2B8022 : COLOURS.red[700],
      badgeColour: COLOURS.white,
    },
    {
      name: isTQArchived
        ? t('withdrawn', 'Withdrawn')
        : t('nearing_deadline', 'Nearing deadline'),
      value: isTQArchived ? statistics.withdrawnCount : statistics.nearingDeadlineCount,
      percentage: isTQArchived
        ? statistics.withdrawnPercentage
        : statistics.nearingDeadlinePercentage,
      colour: isTQ ? CUSTOM_TQ_COLOURS.lightBlue4CAAF0 : COLOURS.yellow[500],
      badgeColour: COLOURS.white,
    },
  ];

  const StatisticsTq = [
    {
      name: t('determined', 'Determined'),
      value: statistics.determinedCount,
      percentage: statistics.determinedPercentage,
      colour: CUSTOM_TQ_COLOURS.lightGreenBDC549,
      badgeColour: COLOURS.white,
    },
  ];

  const finalStats = data.concat(isTQArchived ? StatisticsTq : []);

  const renderStat = (statistic: Statistic) => (
    <Flex
      className='stat-legend-line'
      key={`${statistic.name}`}
      borderBottom={`1px solid ${COLOURS.gray[300]}`}
      px={tvs({ tq: '.75rem' }, 4)}
      py={2}
      w='100%'
      justifyContent='space-between'
      alignItems='center'
    >
      <Typography className='stat-legend-title' fontSize='sm'>
        {statistic.name} <strong>({statistic.value})</strong>
      </Typography>
      <Flex
        className='stat-legend-percentage-container'
        justifyContent='center'
        alignItems='center'
        h='24px'
        w={tvs({ tq: '39px' }, '36px')}
        minW='36px'
        backgroundColor={statistic.colour}
        color={statistic.badgeColour}
        fontWeight={tvs({ tq: 'normal' }, 'bolder')}
      >
        <Typography className='stat-legend-percentage' fontSize='sm'>
          {Math.round(statistic.percentage)}
        </Typography>
        <Typography className='stat-legend-percentage-symbol' fontSize='xs' ml='1px'>
          %
        </Typography>
      </Flex>
    </Flex>
  );

  return (
    <Box
      border={`1px solid ${COLOURS.gray[300]}`}
      borderRadius={tvs({ tq: '3px' }, 5)}
      p={tvs({ tq: '1.25rem' }, 4)}
      boxShadow={tvs({ tq: 'none' }, '0 0 12px -8px rgba(0, 0, 0, 0.35)')}
    >
      <Grid gridGap={4}>
        <Box
          className='chart-header'
          backgroundColor={tvs({ tq: 'revert' }, COLOURS.blue[150])}
          px={tvs({ tq: '.75rem' }, 4)}
          py={tvs({ tq: 0 }, 1)}
        >
          <Typography fontSize='sm' fontWeight='bold'>
            {title}
          </Typography>
        </Box>
        <Flex gridGap={4}>
          <Grid className='chart-container' w='calc(100% - 136px)' alignItems='start'>
            {finalStats.map((statistic) => renderStat(statistic))}
          </Grid>
          <Box position='relative'>
            <Flex
              className='chart-centre-container'
              position='absolute'
              w='100%'
              height={120}
              justifyContent='center'
              alignItems='center'
            >
              <Grid className='chart-centre-text'>
                <Flex>{t('open', 'Open')}:</Flex>
                <Flex justifyContent='center' fontSize='lg'>
                  <strong>{statistics.totalCount}</strong>
                </Flex>
              </Grid>
            </Flex>
            <PieChart width={120} height={120}>
              <Pie
                data={finalStats}
                cx={55}
                cy={55}
                innerRadius={45}
                outerRadius={60}
                paddingAngle={4}
                startAngle={180}
                endAngle={-180}
                isAnimationActive={false}
                dataKey='value'
              >
                {finalStats.map((statistic) => (
                  <Cell key={statistic.name} fill={statistic.colour} />
                ))}
              </Pie>
            </PieChart>
          </Box>
        </Flex>
      </Grid>
    </Box>
  );
};
